import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ListChecks, CaretRight } from "@phosphor-icons/react";

const CLASS_LABELS = {
  discharge_summary: "Discharge Summary",
  hospital_bill: "Hospital Bill",
  hospital_bill_summary: "Summary Bill",
  hospital_bill_detailed: "Detailed Bill",
  pharmacy_bill: "Pharmacy Bill",
  lab_report: "Lab Report",
  prescription: "Prescription",
  pre_hospitalization_bill: "Pre-Hospitalization Bill",
  post_hospitalization_bill: "Post-Hospitalization Bill",
  other: "Other"
};

function DataField({ label, value }) {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const displayValue = Array.isArray(value) ? value.join(", ") : String(value);
  return (
    <div className="py-2">
      <dt className="label-xs mb-0.5">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{displayValue}</dd>
    </div>
  );
}

export default function ExtractedDataTab({ claim, selectedPage, onSelectPage }) {
  const pages = claim.pages || [];
  const isProcessed = claim.status === "processed";

  if (!isProcessed) {
    return (
      <div className="p-6 text-center text-gray-400" data-testid="extracted-data-empty">
        <ListChecks size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-sm">Process the claim to view extracted data.</p>
      </div>
    );
  }

  const currentPage = pages.find(p => p.page_number === selectedPage);
  const pagesWithData = pages.filter(p => p.extracted_data && Object.keys(p.extracted_data).length > 0);

  return (
    <div className="flex h-full" data-testid="extracted-data-tab">
      {/* Page list sidebar */}
      <div className="w-48 flex-shrink-0 border-r border-gray-200 bg-gray-50">
        <div className="p-3">
          <p className="label-xs mb-2">Pages with data</p>
          <div className="space-y-1">
            {pagesWithData.length === 0 ? (
              <p className="text-xs text-gray-400">No data extracted</p>
            ) : (
              pagesWithData.map(p => (
                <button
                  key={p.page_number}
                  onClick={() => onSelectPage(p.page_number)}
                  data-testid={`extracted-page-${p.page_number}`}
                  className={`w-full text-left px-2.5 py-2 rounded text-xs flex items-center justify-between transition-colors ${
                    selectedPage === p.page_number
                      ? "bg-[#002FA7] text-white"
                      : "hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  <span className="truncate">
                    P{p.page_number} - {CLASS_LABELS[p.classification] || p.classification}
                  </span>
                  <CaretRight size={12} />
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Data display */}
      <ScrollArea className="flex-1">
        <div className="p-6">
          {currentPage?.extracted_data && Object.keys(currentPage.extracted_data).length > 0 ? (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Page {selectedPage} - {CLASS_LABELS[currentPage.classification] || currentPage.classification}
                </h3>
                <Badge variant="outline" className="text-xs">
                  {Math.round((currentPage.confidence || 0) * 100)}% confidence
                </Badge>
              </div>

              <div className="border border-gray-200 rounded-md p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 divide-y divide-gray-100">
                  {Object.entries(currentPage.extracted_data).map(([key, value]) => {
                    if (key === "line_items" || key === "medicines" || key === "tests" || key === "_confidence") return null;
                    const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                    return <DataField key={key} label={label} value={value} />;
                  })}
                </div>

                {/* Line items table */}
                {(currentPage.extracted_data.line_items || currentPage.extracted_data.medicines || currentPage.extracted_data.tests) && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <p className="label-xs mb-2">
                      {currentPage.extracted_data.line_items ? "Line Items" :
                       currentPage.extracted_data.medicines ? "Medicines" : "Tests"}
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            {Object.keys(
                              (currentPage.extracted_data.line_items || currentPage.extracted_data.medicines || currentPage.extracted_data.tests)[0] || {}
                            ).map(h => (
                              <th key={h} className="px-2 py-1.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                                {h.replace(/_/g, " ")}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(currentPage.extracted_data.line_items || currentPage.extracted_data.medicines || currentPage.extracted_data.tests).map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              {Object.values(item).map((val, vidx) => (
                                <td key={vidx} className="px-2 py-1.5 text-gray-700">
                                  {typeof val === 'number' ? val.toLocaleString() : String(val)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm">
                {currentPage ? "No detailed data extracted for this page." : "Select a page to view extracted data."}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
